import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Plus,
  Search,
  Edit,
  Trash2,
  ExternalLink,
  Mail,
  Eye,
  TrendingUp,
  AlertTriangle,
  Percent,
  MousePointer,
  Loader2,
  RefreshCw,
  Star,
  Upload,
  Image as ImageIcon,
  FileText,
  Check,
} from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  getPartners,
  createPartner,
  updatePartner,
  deletePartner,
  getRecentActivity,
  subscribeToPartners,
  subscribeToActivity,
  uploadPartnerImage,
  type Partner,
  type ActivityLog,
  type CreatePartnerDTO,
  uploadPartnerPDF,
  deletePartnerPDF,
} from "@/data/partners";

const categories = [
  "Architecture & Design",
  "Technology",
  "F&B Consultants",
  "Operations",
  "Legal & Finance",
  "Development & Construction",
];

const ratingOptions = [
  { value: "5", label: "5 ★ - Excellent" },
  { value: "4.5", label: "4.5 ★ - Very Good" },
  { value: "4", label: "4 ★ - Good" },
  { value: "3.5", label: "3.5 ★ - Above Average" },
  { value: "3", label: "3 ★ - Average" },
  { value: "2.5", label: "2.5 ★ - Below Average" },
  { value: "2", label: "2 ★ - Poor" },
  { value: "1.5", label: "1.5 ★ - Very Poor" },
  { value: "1", label: "1 ★ - Unacceptable" },
];

interface PartnerFormProps {
  isEdit?: boolean;
  formData: CreatePartnerDTO;
  setFormData: React.Dispatch<React.SetStateAction<CreatePartnerDTO>>;
  saving: boolean;
  saveEdit: () => Promise<void>;
  handleAdd: () => Promise<void>;
  setEditDialogOpen: (open: boolean) => void;
  setAddDialogOpen: (open: boolean) => void;
  onPDFUpload: (e: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
  uploadingPdf: boolean;
  onPDFDelete?: () => void;
}

const PartnerForm: React.FC<PartnerFormProps> = React.memo(
  ({
    isEdit = false,
    formData,
    setFormData,
    saving,
    saveEdit,
    handleAdd,
    setEditDialogOpen,
    setAddDialogOpen,
    uploadingPdf,
    onPDFUpload,
    onPDFDelete,
  }) => {
    const [imageUploading, setImageUploading] = useState(false);

    const handleImageUpload = async (
      e: React.ChangeEvent<HTMLInputElement>,
    ) => {
      const file = e.target.files?.[0];
      if (!file) return;

      if (!file.type.startsWith("image/")) {
        toast.error("Please select an image file");
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image size must be less than 5MB");
        return;
      }

      try {
        setImageUploading(true);
        const partnerId = isEdit
          ? formData.name.toLowerCase().replace(/\s+/g, "-")
          : `temp-${Date.now()}`;
        const imageUrl = await uploadPartnerImage(file, partnerId);
        setFormData((prev) => ({ ...prev, main_image: imageUrl }));
        toast.success("Image uploaded successfully");
      } catch (error) {
        console.error("Error uploading image:", error);
        toast.error("Failed to upload image");
      } finally {
        setImageUploading(false);
        e.target.value = "";
      }
    };

    return (
      <div className="space-y-4 py-4">
        <div className="space-y-2">
          <Label>
            Company Name <span className="text-destructive">*</span>
          </Label>
          <Input
            placeholder="e.g., Design Studio XYZ"
            value={formData.name}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, name: e.target.value }))
            }
            disabled={saving}
          />
        </div>

        <div className="space-y-2">
          <Label>Specialty</Label>
          <Input
            placeholder="e.g., Boutique hotel architecture"
            value={formData.specialty}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, specialty: e.target.value }))
            }
            disabled={saving}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>
              Category <span className="text-destructive">*</span>
            </Label>
            <Select
              value={formData.category}
              onValueChange={(value) =>
                setFormData((prev) => ({ ...prev, category: value }))
              }
            >
              <SelectTrigger disabled={saving}>
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((cat) => (
                  <SelectItem key={cat} value={cat}>
                    {cat}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Location</Label>
            <Input
              placeholder="e.g., Barcelona, Spain"
              value={formData.location}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, location: e.target.value }))
              }
              disabled={saving}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Rating</Label>
            <Select
              value={formData.rating?.toString() || ""}
              onValueChange={(value) =>
                setFormData((prev) => ({ ...prev, rating: parseFloat(value) }))
              }
            >
              <SelectTrigger disabled={saving}>
                <SelectValue placeholder="Select rating" />
              </SelectTrigger>
              <SelectContent>
                {ratingOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Project Count</Label>
            <Input
              type="number"
              min="0"
              placeholder="e.g., 50"
              value={formData.project_count || ""}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  project_count: e.target.value ? parseInt(e.target.value) : 0,
                }))
              }
              disabled={saving}
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label>Experience</Label>
          <Input
            placeholder="e.g., 5+ years in hospitality design"
            value={formData.experience || ""}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, experience: e.target.value }))
            }
            disabled={saving}
          />
        </div>

        <div className="space-y-2">
          <Label>Main Image</Label>
          <div className="flex flex-col gap-2">
            <div className="flex gap-2">
              <Input
                placeholder="Image URL or upload file"
                value={formData.main_image || ""}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    main_image: e.target.value,
                  }))
                }
                disabled={saving || imageUploading}
              />
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
                id="image-upload"
                disabled={saving || imageUploading}
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => document.getElementById("image-upload")?.click()}
                disabled={saving || imageUploading}
                className="whitespace-nowrap"
              >
                {imageUploading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Upload className="h-4 w-4 mr-2" />
                )}
                {imageUploading ? "Uploading..." : "Upload"}
              </Button>
            </div>

            {formData.main_image && (
              <div className="mt-2">
                <p className="text-sm text-muted-foreground mb-1">Preview:</p>
                <div className="relative w-32 h-32 border rounded-md overflow-hidden">
                  <img
                    src={formData.main_image}
                    alt="Preview"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src =
                        "https://placehold.co/200x200/4A90E2/FFFFFF?text=No+Image";
                    }}
                  />
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    setFormData((prev) => ({ ...prev, main_image: "" }))
                  }
                  className="mt-2 text-xs"
                >
                  Remove image
                </Button>
              </div>
            )}
          </div>
        </div>

        <div className="space-y-2">
          <Label>Description</Label>
          <Textarea
            placeholder="Describe their expertise and value proposition..."
            value={formData.description}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, description: e.target.value }))
            }
            disabled={saving}
          />
        </div>

        <div className="space-y-2">
          <Label>Website URL</Label>
          <Input
            placeholder="https://..."
            value={formData.website}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, website: e.target.value }))
            }
            disabled={saving}
          />
        </div>

        <div className="space-y-2">
          <Label>Discount Code (optional)</Label>
          <Input
            placeholder="e.g., FOUNDRY15"
            value={formData.discount_code}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                discount_code: e.target.value,
              }))
            }
            disabled={saving}
          />
        </div>
        <div className="space-y-2 border-t pt-4 mt-4">
          <label className="text-sm font-medium flex items-center gap-2">
            <FileText className="h-4 w-4 text-orange-500" />
            Services & Pricing PDF
          </label>

          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <Input
                type="file"
                accept=".pdf"
                onChange={onPDFUpload}
                disabled={uploadingPdf || !formData.name}
                className="cursor-pointer"
              />
              {uploadingPdf && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <Loader2 className="h-4 w-4 animate-spin text-primary" />
                </div>
              )}
            </div>

            {formData.pdf_url && (
              <div className="flex gap-1">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => window.open(formData.pdf_url, "_blank")}
                  title="Preview PDF"
                >
                  <ExternalLink className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => {
                    setFormData((prev) => ({ ...prev, pdf_url: "" }));
                    onPDFDelete?.();
                  }}
                  title="Delete PDF"
                  className="text-destructive hover:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>

          {!formData.name && (
            <p className="text-[10px] text-amber-600">
              * Please enter partner name first to enable PDF upload
            </p>
          )}

          {formData.pdf_url && (
            <p className="text-[10px] text-emerald-600 flex items-center gap-1">
              <Check className="h-3 w-3" /> File linked successfully
            </p>
          )}
        </div>

        <DialogFooter className="pt-4">
          <Button
            variant="outline"
            onClick={() =>
              isEdit ? setEditDialogOpen(false) : setAddDialogOpen(false)
            }
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            className="bg-primary-blue"
            onClick={isEdit ? saveEdit : handleAdd}
            disabled={saving}
          >
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {isEdit ? "Saving..." : "Adding..."}
              </>
            ) : (
              <>{isEdit ? "Save Changes" : "Add Partner"}</>
            )}
          </Button>
        </DialogFooter>
      </div>
    );
  },
);

export default function AdminPartners() {
  const [partners, setPartners] = useState<Partner[]>([]);
  const [activityLogs, setActivityLogs] = useState<ActivityLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState<Partner | null>(null);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [deletingPdf, setDeletingPdf] = useState(false);

  const [formData, setFormData] = useState<CreatePartnerDTO>({
    name: "",
    specialty: "",
    category: "",
    location: "",
    description: "",
    website: "",
    discount_code: "",
    rating: undefined,
    project_count: 0,
    experience: "",
    main_image: "",
    pdf_url: "",
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [partnersData, activityData] = await Promise.all([
        getPartners(),
        getRecentActivity(5),
      ]);
      setPartners(partnersData);
      setActivityLogs(activityData);
    } catch (error: any) {
      console.error("Error loading data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();

    const unsubscribePartners = subscribeToPartners(() => {
      loadData();
    });

    const unsubscribeActivity = subscribeToActivity(() => {
      loadData();
    });

    return () => {
      unsubscribePartners();
      unsubscribeActivity();
    };
  }, [loadData]);

  const filteredPartners = partners.filter(
    (p) =>
      p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.experience?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.specialty?.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const onPDFUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.type !== "application/pdf") {
      toast.error("Please upload only PDF documents");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File is too large (max 10MB)");
      return;
    }

    try {
      setUploadingPdf(true);
      const url = await uploadPartnerPDF(file, formData.name || "unnamed");

      setFormData((prev) => ({ ...prev, pdf_url: url }));
      toast.success("Document uploaded and linked");
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploadingPdf(false);
    }
  };
  const totalViews = partners.reduce((sum, p) => sum + p.views, 0);
  const totalIntros = partners.reduce((sum, p) => sum + p.intro_requests, 0);

  const avgRating =
    partners.length > 0
      ? (
          partners.reduce((sum, p) => sum + (p.rating || 0), 0) /
          partners.length
        ).toFixed(1)
      : "0";
  const topPartner = [...partners].sort(
    (a, b) => b.intro_requests - a.intro_requests,
  )[0];

  const handleEdit = (partner: Partner) => {
    setSelectedPartner(partner);
    setFormData({
      name: partner.name,
      specialty: partner.specialty,
      category: partner.category,
      location: partner.location,
      description: partner.description,
      website: partner.website,
      discount_code: partner.discount_code,
      rating: partner.rating,
      project_count: partner.project_count,
      experience: partner.experience,
      main_image: partner.main_image || "",
      pdf_url: partner.pdf_url || "",
    });
    setEditDialogOpen(true);
  };

  const handleDelete = (partner: Partner) => {
    setSelectedPartner(partner);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = useCallback(async () => {
    if (!selectedPartner) return;

    try {
      setSaving(true);
      await deletePartner(selectedPartner.id);
      toast.success(`"${selectedPartner.name}" removed`);
      loadData();
    } catch (error) {
      console.error("Error deleting partner:", error);
      toast.error("Failed to delete partner");
    } finally {
      setSaving(false);
      setDeleteDialogOpen(false);
      setSelectedPartner(null);
    }
  }, [selectedPartner, loadData]);

  const handlePDFDelete = useCallback(async () => {
    if (!formData.pdf_url) return;

    try {
      setDeletingPdf(true);
      await deletePartnerPDF(formData.pdf_url);

      setFormData((prev) => ({ ...prev, pdf_url: "" }));
      toast.success("PDF file deleted successfully");
    } catch (error) {
      console.error("Error deleting PDF:", error);
      toast.error("Failed to delete PDF file");
    } finally {
      setDeletingPdf(false);
    }
  }, [formData.pdf_url]);
  const saveEdit = useCallback(async () => {
    if (!selectedPartner || !formData.name || !formData.category) {
      toast.error("Please fill all required fields");
      return;
    }

    try {
      setSaving(true);
      await updatePartner(selectedPartner.id, formData);
      toast.success(`"${formData.name}" updated`);
      setEditDialogOpen(false);
      setSelectedPartner(null);
      loadData();
    } catch (error) {
      console.error("Error updating partner:", error);
      toast.error("Failed to update partner");
    } finally {
      setSaving(false);
    }
  }, [selectedPartner, formData, loadData]);

  const resetForm = useCallback(() => {
    setFormData({
      name: "",
      specialty: "",
      category: "",
      location: "",
      description: "",
      website: "",
      discount_code: "",
      rating: undefined,
      project_count: 0,
      experience: "",
      main_image: "",
      pdf_url: "",
    });
  }, []);

  const handleAdd = useCallback(async () => {
    if (!formData.name || !formData.category) {
      toast.error("Please fill all required fields");
      return;
    }

    try {
      setSaving(true);
      await createPartner(formData);
      toast.success(`"${formData.name}" added`);
      setAddDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      console.error("Error creating partner:", error);
      toast.error("Failed to add partner");
    } finally {
      setSaving(false);
    }
  }, [formData, resetForm, loadData]);

  const formatActivityAction = (action: string) => {
    switch (action) {
      case "view":
        return "View";
      case "website_click":
        return "Website Click";
      case "intro_request":
        return "Intro Request";
      default:
        return action;
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const renderRatingStars = (rating: number) => {
    return (
      <div className="flex items-center gap-0.5">
        {[...Array(5)].map((_, i) => (
          <Star
            key={i}
            className={`h-3 w-3 ${
              i < Math.floor(rating)
                ? "text-yellow-500 fill-yellow-500"
                : "text-gray-300"
            }`}
          />
        ))}
        <span className="ml-1 text-xs font-medium">{rating.toFixed(1)}</span>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Vetted Partners</h1>
          <p className="text-muted-foreground mt-1">
            Manage partner listings and track engagement
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            onClick={loadData}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
          <Dialog
            open={addDialogOpen}
            onOpenChange={(open) => {
              setAddDialogOpen(open);
              if (!open) resetForm();
            }}
          >
            <DialogTrigger asChild>
              <Button className="bg-primary-blue">
                <Plus className="mr-2 h-4 w-4" />
                Add Partner
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto p-3">
              <DialogHeader className="pb-4">
                <DialogTitle>Add New Partner</DialogTitle>
                <DialogDescription>
                  Add a vetted partner to the marketplace.
                </DialogDescription>
              </DialogHeader>
              <PartnerForm
                formData={formData}
                setFormData={setFormData}
                saving={saving}
                handleAdd={handleAdd}
                saveEdit={saveEdit}
                setEditDialogOpen={setEditDialogOpen}
                setAddDialogOpen={setAddDialogOpen}
                onPDFUpload={onPDFUpload}
                uploadingPdf={uploadingPdf}
                onPDFDelete={handlePDFDelete}
              />
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-primary">
          <CardContent className="pt-4">
            <p className="text-sm text-muted-foreground">Total Page Views</p>
            <p className="text-2xl font-bold">{totalViews}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-sm text-muted-foreground">Intro Requests</p>
            <p className="text-2xl font-bold">{totalIntros}</p>
            <p className="text-xs text-emerald-600 flex items-center gap-1 mt-1">
              <TrendingUp className="h-3 w-3" />
              High-intent leads
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-sm text-muted-foreground">Avg. Rating</p>
            <p className="text-2xl font-bold flex items-center gap-1">
              {avgRating}
              <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-sm text-muted-foreground">Top Partner</p>
            <p className="text-lg font-semibold truncate">{topPartner?.name}</p>
            <p className="text-xs text-muted-foreground">
              {topPartner?.intro_requests} intros
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search partners..."
          className="pl-10"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">All Partners</CardTitle>
        </CardHeader>
        <CardContent>
          {filteredPartners.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No partners found. Add your first partner above.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-64">Partner</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead className="text-center">Rating</TableHead>
                  <TableHead className="text-center">Projects</TableHead>
                  <TableHead className="text-center">Experience</TableHead>
                  <TableHead className="text-center">Views</TableHead>
                  <TableHead className="text-center">Website Clicks</TableHead>
                  <TableHead className="text-center">Intro Requests</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredPartners.map((partner) => {
                  const conversionRate =
                    partner.views > 0
                      ? (
                          (partner.intro_requests / partner.views) *
                          100
                        ).toFixed(0)
                      : "0";
                  return (
                    <TableRow key={partner.id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          {partner.main_image ? (
                            <div className="relative w-10 h-10 rounded-md overflow-hidden flex-shrink-0">
                              <img
                                src={partner.main_image}
                                alt={partner.name}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).src =
                                    "https://placehold.co/40x40/4A90E2/FFFFFF?text=" +
                                    partner.name.charAt(0);
                                }}
                              />
                            </div>
                          ) : (
                            <div className="w-10 h-10 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0">
                              <ImageIcon className="h-5 w-5 text-primary" />
                            </div>
                          )}
                          <div>
                            <p className="font-medium">{partner.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {partner.location}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{partner.category}</Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        {partner.rating
                          ? renderRatingStars(partner.rating)
                          : "N/A"}
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="font-medium">
                          {partner.project_count || 0}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="text-sm text-muted-foreground max-w-[120px] truncate">
                          {partner.experience || "Not specified"}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Eye className="h-3 w-3 text-muted-foreground" />
                          {partner.views}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <ExternalLink className="h-3 w-3 text-muted-foreground" />
                          {partner.website_clicks}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Mail className="h-3 w-3 text-muted-foreground" />
                          {partner.intro_requests}
                          {Number(conversionRate) > 8 && (
                            <span className="ml-1 text-xs text-emerald-600">
                              ({conversionRate}%)
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => handleEdit(partner)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-destructive hover:text-destructive"
                            onClick={() => handleDelete(partner)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <MousePointer className="h-5 w-5" />
            Recent Activity
          </CardTitle>
        </CardHeader>
        <CardContent>
          {activityLogs.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No activity yet.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Partner</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead className="text-right">Timestamp</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {activityLogs.map((activity) => (
                  <TableRow key={activity.id}>
                    <TableCell className="font-medium">
                      {activity.partner_name}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          activity.action === "intro_request"
                            ? "default"
                            : "secondary"
                        }
                      >
                        {formatActivityAction(activity.action)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right text-muted-foreground">
                      {formatDate(activity.timestamp)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto p-3">
          <DialogHeader className="pb-4">
            <DialogTitle>Edit Partner</DialogTitle>
            <DialogDescription>
              Update partner details and information.
            </DialogDescription>
          </DialogHeader>
          <PartnerForm
            isEdit
            formData={formData}
            setFormData={setFormData}
            saving={saving}
            handleAdd={handleAdd}
            saveEdit={saveEdit}
            setEditDialogOpen={setEditDialogOpen}
            setAddDialogOpen={setAddDialogOpen}
            onPDFUpload={onPDFUpload}
            uploadingPdf={uploadingPdf}
            onPDFDelete={handlePDFDelete}
          />
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader className="pb-4">
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Remove Partner
            </AlertDialogTitle>
            <AlertDialogDescription className="pt-2">
              Are you sure you want to remove "{selectedPartner?.name}" from the
              marketplace? This action cannot be undone.
              {selectedPartner && selectedPartner.intro_requests > 0 && (
                <span className="block mt-2 text-amber-600">
                  ⚠️ This partner has {selectedPartner.intro_requests} intro
                  requests.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={saving}
            >
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Removing...
                </>
              ) : (
                "Remove"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
